import { useState, useEffect, useCallback, useRef } from 'react'
import { checkConnectivity } from '../utils/connectivityCheck'

const RETRY_INTERVAL = 15_000

export function useOnlineStatus() {
 const [isOnline, setIsOnline] = useState(
 typeof navigator !== 'undefined' ? navigator.onLine : true
 )
 const intervalRef = useRef<ReturnType<typeof setInterval>>()

 const verify = useCallback(async () => {
 const real = await checkConnectivity()
 setIsOnline(real)
 }, [])

 useEffect(() => {
 const on = () => { setIsOnline(true); clearInterval(intervalRef.current) }
 const off = () => { setIsOnline(false); void verify() }
 window.addEventListener('online', on)
 window.addEventListener('offline', off)

 // On mount: if browser says offline, double-check with a real request
 if (typeof navigator !== 'undefined' && !navigator.onLine) void verify()

 return () => {
 window.removeEventListener('online', on)
 window.removeEventListener('offline', off)
 clearInterval(intervalRef.current)
 }
 }, [verify])

 // While offline, poll every RETRY_INTERVAL to catch cases where the
 // browser never fires the"online"event after network recovery
 useEffect(() => {
 if (!isOnline) {
 intervalRef.current = setInterval(() => void verify(), RETRY_INTERVAL)
 return () => clearInterval(intervalRef.current)
 }
 }, [isOnline, verify])

 return { isOnline, isOffline: !isOnline }
}

export function useOnBackOnline(callback: () => void) {
 const handler = useCallback(() => {
 callback()
 }, [callback])

 useEffect(() => {
 window.addEventListener('online', handler)
 return () => window.removeEventListener('online', handler)
 }, [handler])
}
